/**
 * Dunea Domain detail page (DEV-121) — a dedicated, shareable page for a single
 * domain at `/domains/:domain` (replaces the old side drawer). Resolves the domain
 * from the managed list, then renders the full tabbed management view.
 *
 * Presentation only — uses the existing domains APIs.
 */
import { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { classNames } from '~/utils/classNames';
import { apiListDomains, apiGetRegistrant, apiResumeDomainCheckout, apiRemoveDomain } from '~/lib/api/domains';
import { apiGetBillingOverview } from '~/lib/api/billing';
import { StatusPill } from '~/components/domains/domainUi';
import { DomainDetailBody } from '~/components/domains/DomainDetailView';

export default function DomainDetailPage() {
  const { domain: domainParam } = useParams<{ domain: string }>();
  const decoded = decodeURIComponent(domainParam ?? '');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: managed = [], isLoading } = useQuery({ queryKey: ['domains'], queryFn: apiListDomains });
  const { data: registrant } = useQuery({ queryKey: ['registrant'], queryFn: apiGetRegistrant });
  const { data: overview } = useQuery({ queryKey: ['billing-overview'], queryFn: apiGetBillingOverview });

  const domain = useMemo(() => managed.find((d) => d.domain === decoded) ?? null, [managed, decoded]);

  const resumeMutation = useMutation({
    mutationFn: (id: string) => apiResumeDomainCheckout(id),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not resume your checkout. Please try again.');
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => apiRemoveDomain(id),
    onSuccess: () => {
      toast.success('Order removed.');
      queryClient.invalidateQueries({ queryKey: ['domains'] });
      navigate('/domains');
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not remove this order.');
    },
  });

  const visitHref = domain?.deployUrl
    ? domain.source === 'connected'
      ? `https://${domain.domain}`
      : domain.deployUrl
    : null;
  const canVisit = visitHref && domain?.status !== 'pending';

  return (
    <div className="relative min-h-dvh overflow-x-hidden bg-page">
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-[-160px] h-[380px] w-[760px] max-w-full -translate-x-1/2 rounded-full bg-accent/8 blur-[140px]"
      />

      <div className="relative mx-auto max-w-3xl px-4 pb-24 pt-6 sm:px-5 sm:pt-8">
        {/* Back */}
        <Link to="/domains" className="ui-btn-ghost mb-8 inline-flex">
          <div className="i-ph:arrow-left text-[13px]" />
          All domains
        </Link>

        {isLoading ? (
          <div className="flex items-center gap-2 text-[13px] text-muted">
            <div className="i-svg-spinners:90-ring-with-bg" />
            Loading domain…
          </div>
        ) : !domain ? (
          <div className="rounded-2xl border border-hairline bg-raised px-6 py-14 text-center">
            <div className="i-ph:globe-x mx-auto mb-3 text-4xl text-faint" />
            <h1 className="ui-serif text-[22px] text-primary">Domain not found</h1>
            <p className="mx-auto mt-2 max-w-sm text-[13px] text-muted">
              We couldn’t find <span className="ui-mono text-secondary">{decoded}</span> in your account.
            </p>
            <Link to="/domains" className="ui-btn-accent mt-6 inline-flex">
              Back to domains
            </Link>
          </div>
        ) : (
          <>
            {/* Domain hero header — premium glass card */}
            <div className="relative mb-8 overflow-hidden rounded-3xl border border-hairline bg-gradient-to-br from-accent/[0.06] via-raised to-raised p-4 shadow-[0_1px_0_0_rgba(255,255,255,0.04)_inset,0_24px_60px_-40px_rgba(0,0,0,0.9)] sm:p-6">
              <div
                aria-hidden
                className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full bg-accent/10 blur-3xl"
              />
              <div className="relative flex flex-wrap items-center justify-between gap-4">
                <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-accent/12 ring-1 ring-inset ring-accent/25 sm:h-16 sm:w-16">
                    <div className="i-ph:globe text-[22px] text-accent sm:text-[28px]" />
                  </div>
                  <div className="min-w-0">
                    {/* Wraps instead of truncating — this page must show the whole domain. */}
                    <h1 className="ui-mono break-all text-[18px] font-medium text-primary sm:text-[24px]">
                      {domain.domain}
                    </h1>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <StatusPill status={domain.status} />
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-hairline bg-raised/70 px-2.5 py-0.5 text-[11px] text-muted">
                        <div
                          className={classNames(
                            domain.source === 'purchased' ? 'i-ph:shopping-bag-open' : 'i-ph:link-simple',
                            'text-[12px]',
                          )}
                        />
                        {domain.source === 'purchased' ? 'Purchased with Dunea' : 'Connected domain'}
                      </span>
                    </div>
                  </div>
                </div>
                {canVisit && (
                  <a href={visitHref!} target="_blank" rel="noreferrer" className="ui-btn-accent">
                    <div className="i-ph:arrow-square-out text-[14px]" />
                    Visit site
                  </a>
                )}
              </div>
            </div>

            <DomainDetailBody
              domain={domain}
              registrant={registrant}
              paymentMethod={overview?.paymentMethod}
              onResume={() => domain.id && resumeMutation.mutate(domain.id)}
              onRemove={() => domain.id && removeMutation.mutate(domain.id)}
              busy={resumeMutation.isPending || removeMutation.isPending}
            />
          </>
        )}
      </div>
    </div>
  );
}
